import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Divider,
  HStack,
  Heading,
  Icon,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { format, isPast, parseISO } from 'date-fns';
import { useListClientServices } from 'hooks/useApiQueries';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ClientService } from '../../../types/client';

export function ServicesHistory(): JSX.Element {
  const { data: servicesData } = useListClientServices();
  const services = servicesData ? Object.values(servicesData) : [];
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { activeServices, expiredServices } = useMemo(() => {
    if (!services) return { activeServices: [], expiredServices: [] };

    const active: ClientService[] = [];
    const expired: ClientService[] = [];

    services.forEach((service: ClientService) => {
      if (isPast(parseISO(service.ExpirationDate))) {
        expired.push(service);
      } else {
        active.push(service);
      }
    });

    return {
      activeServices: active.sort((a, b) => new Date(b.ActiveDate).getTime() - new Date(a.ActiveDate).getTime()),
      expiredServices: expired.sort((a, b) => new Date(b.ActiveDate).getTime() - new Date(a.ActiveDate).getTime()),
    };
  }, [services]);

  if (!services || services.length === 0) {
    return (
      <Button variant="transparent" onClick={() => navigate('/tarifs')}>
        Voir nos tarifs
      </Button>
    );
  }

  return (
    <Stack className="activeServicesHistory">
      {activeServices.length > 0 && (
        <Box my={4}>
          <HStack width="100%" justifyContent="space-between" margin="1em 0 2em">
            <Heading className="accountMainTitle" size="md" textTransform="uppercase">
              {t('Crédits Actifs')}
            </Heading>
            <Button variant="contained" onClick={() => navigate(-1)}>
              Retour
            </Button>
          </HStack>
          <Services services={activeServices} />
        </Box>
      )}

      {expiredServices.length > 0 && activeServices.length > 0 && <Divider />}

      {expiredServices.length > 0 && (
        <Box my={4} className="expiredServicesHistory">
          <Heading textTransform="uppercase" size="md">
            {t('Crédits Expirés')}
          </Heading>
          <Services services={expiredServices} />
        </Box>
      )}
    </Stack>
  );
}

function Services({ services }: { services: ClientService[] }) {
  const { t } = useTranslation();

  const isMobile = useBreakpointValue({ base: true, md: false }) || false;
  const [showDetails, setShowDetails] = useState<Set<string>>(new Set());

  const handleToggleDetails = (serviceId: string) => {
    setShowDetails((prevState) => {
      const newSet = new Set(prevState); // Crée une copie du Set actuel
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId); // Si l'ID est déjà présent, le supprimer (fermer les détails)
      } else {
        newSet.add(serviceId); // Sinon, ajouter l'ID (ouvrir les détails)
      }
      return newSet;
    });
  };

  const rotateIcon = showDetails ? 'rotate(180deg)' : 'rotate(0)';

  return (
    <Table variant="simple">
      <Thead>
        <Tr>
          <Th>{t('Nom du pack')}</Th>
          <Th isNumeric>{t('Sessions restantes')}</Th>
          {!isMobile && (
            <>
              <Th>{t("Date d'activation")}</Th>
              <Th>{t("Date d'expiration")}</Th>
            </>
          )}
        </Tr>
      </Thead>
      <Tbody>
        {services.map((service, index) => (
          <React.Fragment key={index}>
            <Tr>
              <Td>{service.Name}</Td>
              <Td isNumeric>
                {service.Remaining}
                {isMobile && (
                  <Button onClick={() => handleToggleDetails(service.Id.toString())} variant="ghost" size="md">
                    <Icon as={ChevronDownIcon} transition="transform .2s ease-in-out" transform={rotateIcon} />
                  </Button>
                )}
              </Td>
              {!isMobile && (
                <>
                  <Td>{format(parseISO(service.ActiveDate), 'dd/MM/yyyy')}</Td>
                  <Td>{format(parseISO(service.ExpirationDate), 'dd/MM/yyyy')}</Td>
                </>
              )}
            </Tr>
            {isMobile && showDetails.has(service.Id.toString()) && (
              <Tr key={`details-${service.Id}`}>
                <Td colSpan={4}>
                  <VStack align="start" spacing={2}>
                    <Text>
                      <strong>Date d'activation :</strong> {format(parseISO(service.ActiveDate), 'dd/MM/yyyy')}
                    </Text>
                    <Text>
                      <strong>Date d'expiration :</strong> {format(parseISO(service.ExpirationDate), 'dd/MM/yyyy')}
                    </Text>
                  </VStack>
                </Td>
              </Tr>
            )}
          </React.Fragment>
        ))}
      </Tbody>
    </Table>
  );
}
